import React, { useState, useEffect } from 'react';
import { ConversationQuestion, ConversationQuestionOption } from '../../services/conversationService';

interface DialogueQuestionProps {
  question: ConversationQuestion;
  onAnswerSelected: (optionIndex: number, isCorrect: boolean, points: number, nextIndex?: number) => void;
  isLatest: boolean;
  disabled: boolean;
  showCorrectAnswer?: boolean;
  wrapperClassName?: string;
}

const DialogueQuestion: React.FC<DialogueQuestionProps> = ({ 
  question, 
  onAnswerSelected, 
  isLatest,
  disabled,
  showCorrectAnswer = false,
  wrapperClassName
}) => {
  const [visible, setVisible] = useState(!isLatest);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [showResponse, setShowResponse] = useState(false);
  const [responseText, setResponseText] = useState('');
  
  // Animate entrance for latest question
  useEffect(() => {
    if (isLatest) {
      const timer = setTimeout(() => {
        setVisible(true);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [isLatest]);
  
  const handleOptionSelected = (option: ConversationQuestionOption, index: number) => {
    if (disabled || selectedOption !== null) return;
    
    setSelectedOption(index);
    setResponseText(option.responseText || '');
    setShowResponse(true);
    
    // Notify parent component
    onAnswerSelected(
      index, 
      option.isCorrect, 
      option.points,
      option.nextDialogueIndex
    );
  };
  
  if (!visible) return null;
  
  return (
    <div className={`dialogue-question-container ${isLatest ? 'latest-question' : ''} ${disabled ? 'question-disabled' : ''} ${wrapperClassName || ''}`}>
      <div className="question-bubble">
        <div className="question-text">{question.text}</div>
        
        <div className="question-options">
          {question.options.map((option, index) => (
            <button
              key={index}
              onClick={() => handleOptionSelected(option, index)}
              disabled={disabled || selectedOption !== null}
              className={`question-option ${selectedOption === index ? 'selected' : ''} ${
                (showResponse || showCorrectAnswer) && (
                  selectedOption === index 
                    ? (option.isCorrect ? 'correct' : 'incorrect')
                    : option.isCorrect ? 'correct' : ''
                )
              }`}
            >
              <span className="option-letter">
                {String.fromCharCode(65 + index)}
              </span>
              <span className="option-text">{option.text}</span>
            </button>
          ))}
        </div>
        
        {showResponse && responseText && (
          <div className="response-text">
            {responseText}
          </div>
        )}
        
        {(showResponse || showCorrectAnswer) && question.explanation && (
          <div className="explanation-text">
            {question.explanation}
          </div>
        )}
      </div>
      
      {(showResponse || showCorrectAnswer) && question.bibleReference && (
        <div className="bible-reference">
          <div className="reference-label">
            {question.bibleReference.book} {question.bibleReference.chapter}:{question.bibleReference.verses}
          </div>
          {question.bibleReference.text && (
            <div className="reference-text">
              {question.bibleReference.text}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DialogueQuestion; 